import React from 'react';
import { FolderOpen, Filter, MousePointerClick } from 'lucide-react';
import { Condition } from 'types/segments/segments.types';
import { useSelector } from 'react-redux';
import { selectSegments } from 'store/segments';
import Badge from 'components/ui/Badge';

export interface TriggerConditionDisplayProps {
  conditions: { [key: string]: Condition[] };
  /** When 'event', show event name and optional event filters; when 'attribute-segment', show condition tree only */
  triggerType?: 'event' | 'attribute-segment';
  /** Event name to show when triggerType === 'event' */
  eventName?: string;
  /** 'default' for full styling (Review, Dashboard), 'compact' for modals */
  variant?: 'default' | 'compact';
}

const TriggerConditionDisplay: React.FC<TriggerConditionDisplayProps> = ({
  conditions,
  triggerType = 'attribute-segment',
  eventName = '',
  variant = 'default',
}) => {
  const segments = useSelector(selectSegments);

  const isGroupCondition = (condition: Condition): boolean => {
    return condition.condition === 'group';
  };

  const isSegmentCondition = (condition: Condition): boolean => {
    return segments.some((segment) => segment._id === condition.field);
  };

  const getSegmentName = (segmentId: string): string => {
    const segment = segments.find((seg) => seg._id === segmentId);
    return segment ? segment.name : segmentId;
  };

  // Operator labels aligned with ConditionDisplay (SegmentDetails)
  const getOperatorLabel = (operator: string): string => {
    const operatorLabels: { [key: string]: string } = {
      in_segment: 'is in segment',
      not_in_segment: 'is not in segment',
      equals: 'equals',
      not_equals: 'does not equal',
      contains: 'contains',
      not_contains: 'does not contain',
      starts_with: 'starts with',
      ends_with: 'ends with',
      greater_than: 'is greater than',
      less_than: 'is less than',
      greater_than_or_equal: 'is greater than or equal to',
      less_than_or_equal: 'is less than or equal to',
      between: 'is between',
      is_timestamp: 'is timestamp',
      is_not_timestamp: 'is not timestamp',
      is_before: 'is before',
      is_after: 'is after',
      is_between: 'is between',
      exists: 'exists',
      not_exists: 'does not exist',
      is_empty: 'is empty',
      is_not_empty: 'is not empty',
      has_property: 'has the property',
      at_least_one: 'at least one',
    };
    return operatorLabels[operator] || operator;
  };

  const isNestedConditionValue = (
    val: unknown
  ): val is {
    operator: string;
    value?: unknown;
    property?: string;
    arrayType?: string;
  } => {
    return (
      typeof val === 'object' &&
      val !== null &&
      'operator' in (val as object) &&
      typeof (val as { operator: unknown }).operator === 'string'
    );
  };

  const buildPathAndLeaf = (
    rootField: string,
    conditionValue: unknown
  ): { path: string; operator: string; value: unknown } | null => {
    if (!isNestedConditionValue(conditionValue)) return null;
    const root = (rootField || '').replace(/^attributes\./, '');
    let path = root;
    let node: unknown = conditionValue;

    while (node != null && isNestedConditionValue(node)) {
      const op = node.operator;
      const innerVal = node.value;
      const prop = node.property;
      const arrayType = node.arrayType;

      if (op === 'at_least_one') {
        if (prop) path += `.${prop}`;
        if (arrayType === 'arrayValue') path += '[*]';
      } else if (op === 'has_property' && prop) {
        path += `.${prop}`;
      }

      if (innerVal != null && isNestedConditionValue(innerVal)) {
        node = innerVal;
      } else {
        const leafPath = prop ? `${path}.${prop}` : path;
        return { path: leafPath, operator: op, value: innerVal };
      }
    }

    if (node != null && isNestedConditionValue(node)) {
      const prop = node.property;
      const leafPath = prop ? `${path}.${prop}` : path;
      return { path: leafPath, operator: node.operator, value: node.value };
    }
    return null;
  };

  const formatValue = (value: any): string => {
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value !== null) {
      if (value.start && value.end) {
        const formatter = new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: value.timezone || 'UTC',
          second: '2-digit',
        });
        return `${formatter.format(new Date(value.start))} to ${formatter.format(new Date(value.end))} ${value.timezone ?? ''}`;
      }
      if (
        value.dateType === 'relative_date' &&
        value.days != null &&
        value.days !== ''
      ) {
        const days = value.days;
        const dir = value.timeDirection;
        const directionLabel =
          dir === 'from_now' ? 'from now' : dir === 'ago' ? 'ago' : dir || '';
        return directionLabel
          ? `${days} days ${directionLabel}`
          : `${days} days`;
      }
      if (value.dateType === 'specific_value') {
        const date = new Intl.DateTimeFormat('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: value.timezone || 'UTC',
          second: '2-digit',
        }).format(
          value.datetime ? new Date(value.datetime) : new Date(value.date)
        );
        return date + ' ' + (value.timezone ?? '');
      }
      return JSON.stringify(value);
    }
    return String(value);
  };

  const formatNestedConditionAsPath = (
    rootField: string,
    conditionValue: unknown
  ): string | null => {
    const result = buildPathAndLeaf(rootField, conditionValue);
    if (!result) return null;
    const { path, operator, value } = result;
    const valuePart =
      operator === 'exists' ||
      operator === 'not_exists' ||
      operator === 'is_empty' ||
      operator === 'is_not_empty'
        ? ''
        : value != null
          ? ` ${formatValue(value)}`
          : '';
    return `${path} ${getOperatorLabel(operator)}${valuePart}`.trim();
  };

  const generateGroupId = (
    index: number,
    level: number = 0,
    parentPath: string = ''
  ) =>
    parentPath ? `${parentPath}-group-${index}` : `group-${level}-${index}`;

  const renderCondition = (
    condition: Condition,
    index: number,
    level: number = 0,
    parentPath: string = '',
    isInGroup: boolean = false
  ) => {
    const { field, operator, value } = condition;

    if (isGroupCondition(condition)) {
      const groupId = generateGroupId(index, level, parentPath);
      const groupType = (value as any)?.groupType || 'and';
      const groupConditions = (value as any)?.conditions || [];

      return (
        <div
          key={groupId}
          className={`rounded-xl border overflow-hidden shadow-sm transition-shadow ${level === 0 ? 'border-primary/40 bg-card' : 'border-border bg-muted/30'} ${isInGroup ? 'ml-5 border-l-4 border-l-primary/50' : ''}`}
        >
          <div
            className={`flex items-center gap-3 px-4 py-3 border-b ${level === 0 ? 'bg-primary/5 border-border' : 'bg-muted/50 border-border'}`}
          >
            <div
              className={`flex items-center justify-center w-8 h-8 rounded-lg ${level === 0 ? 'bg-primary/15 text-primary' : 'bg-primary/10 text-primary'}`}
            >
              <FolderOpen className="w-4 h-4 shrink-0" />
            </div>
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <Badge
                variant={level === 0 ? 'active' : 'info'}
                size="small"
                className="shrink-0"
              >
                {groupType === 'and' ? 'All' : 'Any'}
              </Badge>
              <span className="text-sm font-medium text-foreground">
                of the following conditions match
              </span>
              {level > 0 && (
                <Badge variant="default" size="small" className="shrink-0">
                  Nested
                </Badge>
              )}
            </div>
          </div>
          <div className="p-4 space-y-3">
            {groupConditions.map(
              (groupCondition: Condition, conditionIndex: number) => (
                <React.Fragment key={`${groupId}-condition-${conditionIndex}`}>
                  {renderCondition(
                    groupCondition,
                    conditionIndex,
                    level + 1,
                    groupId,
                    true
                  )}
                </React.Fragment>
              )
            )}
            {groupConditions.length === 0 && (
              <div className="text-center text-muted-foreground text-sm py-6 rounded-lg bg-muted/20 border border-dashed border-border">
                No conditions in this group
              </div>
            )}
          </div>
        </div>
      );
    }

    const isSegment = isSegmentCondition(condition);
    const isNestedOperator =
      operator === 'has_property' || operator === 'at_least_one';
    const pathDisplay =
      isNestedOperator && isNestedConditionValue(value)
        ? formatNestedConditionAsPath(field ?? '', value)
        : null;
    const displayValue =
      pathDisplay != null ? pathDisplay : formatValue(value ?? '');

    return (
      <div
        key={`${parentPath}-condition-${index}`}
        className={`flex items-center gap-3 p-3 rounded-lg border bg-card border-border transition-colors hover:bg-muted/20 ${isInGroup ? 'ml-0 border-l-4 border-l-primary/40' : ''}`}
      >
        <div className="flex items-center justify-center w-8 h-8 rounded-md bg-muted/80 shrink-0">
          <Filter className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="flex items-center gap-2 flex-wrap min-w-0 text-sm">
          {isSegment ? (
            <>
              <span className="font-medium text-foreground">
                {getSegmentName(field || '')}
              </span>
              <Badge variant="default" size="small" className="font-normal">
                {getOperatorLabel(operator)}
              </Badge>
            </>
          ) : isNestedOperator && pathDisplay != null ? (
            <span className="text-foreground">{displayValue}</span>
          ) : (
            <>
              <span className="font-medium text-foreground truncate max-w-[200px]">
                {field}
              </span>
              <Badge
                variant="info"
                size="small"
                className="font-normal shrink-0"
              >
                {getOperatorLabel(operator)}
              </Badge>
              <span className="text-foreground font-medium break-all">
                {displayValue}
              </span>
            </>
          )}
        </div>
      </div>
    );
  };

  const hasConditions =
    conditions && (conditions.and?.length > 0 || conditions.or?.length > 0);
  const mainConditionType = Object.keys(conditions || {})[0] || 'and';
  const conditionList = (conditions || {})[mainConditionType] || [];

  // Event trigger: show event name, then optional filters
  if (triggerType === 'event') {
    return (
      <div className="space-y-3">
        {eventName && (
          <div className="inline-flex items-center gap-2 px-3 py-2 bg-muted border border-border rounded-md">
            <MousePointerClick className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-foreground">
              {eventName}
            </span>
          </div>
        )}
        {hasConditions && conditionList.length > 0 && (
          <>
            <p className="text-xs font-medium text-muted-foreground">
              Event filters:
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Badge
                  variant={mainConditionType === 'and' ? 'active' : 'info'}
                  size="small"
                >
                  {mainConditionType === 'and' ? 'All' : 'Any'}
                </Badge>
                <span className="text-sm font-medium text-foreground">
                  of the following conditions match
                </span>
              </div>
              {conditionList.map((condition, index) => (
                <React.Fragment key={`root-condition-${index}`}>
                  {renderCondition(condition, index, 0)}
                </React.Fragment>
              ))}
            </div>
          </>
        )}
        {!eventName && !hasConditions && (
          <div className="text-center text-muted-foreground py-2 text-sm">
            No event selected.
          </div>
        )}
      </div>
    );
  }

  // Attribute-segment trigger: show condition tree only
  if (!hasConditions || conditionList.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-6 text-sm">
        <p className="font-medium">
          No trigger conditions set for this campaign.
        </p>
        {variant === 'default' && (
          <p className="text-xs mt-1">
            Edit the workflow to set up trigger conditions.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Badge
          variant={mainConditionType === 'and' ? 'active' : 'info'}
          size="medium"
        >
          {mainConditionType === 'and' ? 'All' : 'Any'}
        </Badge>
        <span className="text-sm font-medium text-foreground">
          of the following conditions match
        </span>
      </div>
      <div className="space-y-3">
        {conditionList.map((condition, index) => (
          <React.Fragment key={`root-condition-${index}`}>
            {renderCondition(condition, index, 0)}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default TriggerConditionDisplay;
